const HUBSPOT_SCRIPT_ID = 'hs-web-interactives-loader'
const HUBSPOT_SCRIPT_URL = 'https://js.hubspot.com/web-interactives-embed.js'
const HUBSPOT_AUX_IDS = [
  'hs-web-interactives-top-anchor',
  'hs-web-interactives-top-push-anchor',
  'hs-web-interactives-bottom-anchor',
  'hs-web-interactives-floating-container',
]

const DEFAULT_RENDER_TIMEOUT = 300

type Nullable<T> = T | null | undefined

let hubspotLoaderPromise: Promise<void> | null = null

export const isBrowserEnvironment = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined'

function removeExistingArtifacts() {
  const removeById = (id: string) => {
    const node = document.getElementById(id)
    if (node && node.parentNode) {
      node.parentNode.removeChild(node)
    }
  }

  removeById(HUBSPOT_SCRIPT_ID)
  HUBSPOT_AUX_IDS.forEach(removeById)
}

function getExistingLoaderScript(): Nullable<HTMLScriptElement> {
  const existingScript = document.getElementById(HUBSPOT_SCRIPT_ID)
  if (existingScript && existingScript.tagName === 'SCRIPT') {
    return existingScript as HTMLScriptElement
  }

  return document.querySelector(
    `script[src="${HUBSPOT_SCRIPT_URL}"]`
  ) as Nullable<HTMLScriptElement>
}

function createLoaderPromise(script: HTMLScriptElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const clearPromise = () => {
      hubspotLoaderPromise = null
    }

    const handleLoad = () => {
      script.dataset.hubspotLoaded = 'true'
      clearPromise()
      resolve()
    }

    const handleError = (error: unknown) => {
      clearPromise()
      reject(error || new Error('Failed to load HubSpot web interactives script'))
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError as EventListener, { once: true })
  })
}

export function resetHubspotLoader(options: { hardReset?: boolean } = {}): Promise<void> {
  if (!isBrowserEnvironment()) {
    return Promise.resolve()
  }

  if (hubspotLoaderPromise) {
    return hubspotLoaderPromise
  }

  const hardReset = options.hardReset ?? false
  const existingScript = getExistingLoaderScript()
  const hubspotWindow = window as any

  if (!hardReset && existingScript) {
    const scriptReadyState = (existingScript as { readyState?: string }).readyState
    const isAlreadyLoaded =
      existingScript.dataset.hubspotLoaded === 'true' ||
      hubspotWindow.hubspot_web_interactives_running === true ||
      scriptReadyState === 'loaded' ||
      scriptReadyState === 'complete'
    if (isAlreadyLoaded) {
      return Promise.resolve()
    }

    hubspotLoaderPromise = createLoaderPromise(existingScript)
    return hubspotLoaderPromise
  }

  hubspotLoaderPromise = new Promise((resolve, reject) => {
    removeExistingArtifacts()
    hubspotWindow.hubspot_web_interactives_running = false

    const script = document.createElement('script')
    script.id = HUBSPOT_SCRIPT_ID
    script.async = true
    script.src = HUBSPOT_SCRIPT_URL

    script.onload = () => {
      script.dataset.hubspotLoaded = 'true'
      hubspotLoaderPromise = null
      resolve()
    }

    script.onerror = error => {
      hubspotLoaderPromise = null
      script.remove()
      reject(error || new Error('Failed to load HubSpot web interactives script'))
    }

    document.body.appendChild(script)
  })

  return hubspotLoaderPromise
}

export function isHubspotContentReady(container: Nullable<Element>): boolean {
  if (!isBrowserEnvironment() || !container) return false

  return Boolean(
    container.classList.contains('hs-cta-embed__loaded') ||
      container.querySelector('iframe') ||
      container.querySelector('.hs-cta-node') ||
      container.querySelector('.hs-cta-wrapper') ||
      container.querySelector('[data-hubspot-loaded="true"]')
  )
}

export function monitorHubspotHydration(
  container: Nullable<Element>,
  options: { timeout?: number } = {}
): { promise: Promise<void>; cleanup: () => void } {
  const timeout = options.timeout ?? DEFAULT_RENDER_TIMEOUT

  let observer: Nullable<MutationObserver> = null
  let timer: Nullable<ReturnType<typeof setTimeout>> = null

  const cleanup = () => {
    if (observer) observer.disconnect()
    observer = null
    if (timer) {
      clearTimeout(timer)
    }
    timer = null
  }

  const promise = new Promise<void>((resolve, reject) => {
    if (!isBrowserEnvironment()) {
      cleanup()
      resolve()
      return
    }

    if (!container) {
      cleanup()
      reject(new Error('HubSpot container was not found'))
      return
    }

    const resolveSafely = () => {
      cleanup()
      resolve()
    }

    const rejectSafely = (error: unknown) => {
      cleanup()
      reject(error instanceof Error ? error : new Error(String(error)))
    }

    const checkReady = () => {
      if (isHubspotContentReady(container)) {
        resolveSafely()
      }
    }

    observer = new MutationObserver(checkReady)
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    })

    timer = setTimeout(() => {
      rejectSafely(new Error(`HubSpot interactive did not finish loading within ${timeout}ms`))
    }, timeout)

    // Run an initial check in case the content is already ready.
    checkReady()
  })

  return { promise, cleanup }
}
